'use client'

import { cn } from '@/lib/utils'
import { useStorePopUp } from '@/store'
import { Typo3PopUp } from '@/types'
import { Icon } from '@/ui/components'
import { delay } from '@/utils'
import { Suspense, useCallback, useEffect, useRef } from 'react'
import BaseLink from '../Base/BaseLink'
import Button from '../Button/Button'
import Img from '../Img/Img'

interface Props {
  data: Typo3PopUp
}

const PopUp = ({ data }: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  // Zustand aus Store
  const { isHidden, setIsHidden } = useStorePopUp()

  // Popup nach x Sekunden anzeigen, wenn es nicht bereits geschlossen wurde
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('popUpDismissed')
    const lastShownDate = sessionStorage.getItem('popUpLastShown')

    const oneDayInMillis = 24 * 60 * 60 * 1000 // 1 Tag in Millisekunden
    const currentTime = new Date().getTime()

    if (
      !wasDismissed ||
      (lastShownDate && currentTime - parseInt(lastShownDate) > oneDayInMillis)
    ) {
      const timer = setTimeout(() => setIsHidden(false), 1000) // Popup erscheint nach 10 Sekunden
      sessionStorage.setItem('popUpLastShown', currentTime.toString())
      return () => clearTimeout(timer)
    }
  }, [data, setIsHidden])

  const handleHidden = useCallback(async () => {
    setIsHidden(true)
    await delay(1000)
    sessionStorage.setItem('popUpDismissed', 'true') // Popup als geschlossen markieren
  }, [setIsHidden])

  // ESC-Taste zum Schließen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleHidden()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleHidden])

  if (!data || data.length === 0) {
    return null
  }

  return (
    <Suspense>
      <div
        className={cn(
          'invisible fixed inset-0 z-[751] flex w-full items-center justify-center overflow-y-auto opacity-0 transition-all duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]',
          !isHidden && 'visible opacity-100 [&_.popup]:translate-y-0'
        )}
        ref={ref}
      >
        <div
          className="pointer-events-none fixed inset-0 z-[-1] bg-[rgb(0,0,0,0.4)]"
          onClick={handleHidden}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleHidden()
            }
          }}
          aria-label="Close popup"
        ></div>
        <div className="popup p-gutter relative z-[752] w-full max-w-[42.75rem] translate-y-32 transition-all duration-[var(--duration-default)] ease-[var(--ease-out-cubic)]">
          <button
            className="mb-1 ml-auto flex h-12 w-12 cursor-pointer items-center justify-center bg-neutral-100 shadow-[0_2px_5px_rgb(0,0,0,0.2)] [&_svg]:h-5 [&_svg]:w-5 [&_svg]:transition-transform [&_svg]:duration-[var(--duration-default)] [&_svg]:ease-[var(--ease-out-cubic)] hover:[&_svg]:rotate-180"
            onClick={handleHidden}
            type="button"
            aria-label="Close popup"
          >
            <Icon icon={'material-symbols:close'} />
          </button>
          <div
            className="max-h-[min(39rem,80vh)] overflow-y-auto bg-neutral-100"
            data-lenis-prevent
          >
            {data[0]?.link?.href &&
            data[0]?.link?.title &&
            data[0]?.popupImage?.[0] ? (
              <BaseLink
                className="w-full"
                onClick={handleHidden}
                href={data[0]?.link?.href}
                target={data[0]?.link?.target || undefined}
                nextLink={data[0]?.link?.target === '_blank' ? false : true}
                isTransition={data[0]?.link?.target === '_blank' ? false : true}
              >
                <Img
                  data={data[0]?.popupImage?.[0]}
                  naked
                  className="h-auto w-full"
                />
              </BaseLink>
            ) : (
              <Img
                data={data[0]?.popupImage?.[0]}
                naked
                className="h-auto w-full"
              />
            )}
            <div className="gap-xl max-md:gap-lg p-gutter flex flex-grow flex-col items-start text-left">
              <div className="gap-md max-md:gap-sm flex flex-col">
                {data[0]?.title && <p className={'h2'}>{data[0]?.title}</p>}
                {data[0]?.description && <p>{data[0]?.description}</p>}
              </div>
              {data[0]?.link?.href && data[0]?.link?.title && (
                <Button
                  href={data[0]?.link?.href}
                  onClick={handleHidden}
                  target={data[0]?.link?.target || undefined}
                  nextLink={data[0]?.link?.target !== '_blank'}
                  isTransition={data[0]?.link?.target !== '_blank'}
                >
                  {data[0]?.link?.title}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  )
}

export default PopUp
